/**
 * Page combinée Détail/Modification d'une Leçon
 *
 * - Mode lecture par défaut
 * - Bouton Modifier → mode édition inline
 * - Création nouvelle leçon si id === 'new' (chapterId requis en query param)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel } from '@mui/icons-material';
import { BookLesson, BookChapter } from '../../types';
import BookContentApiService from '../../services/bookContentApi';
import SchoolBooksApiService from '../../services/schoolBooksApi';
import { toUserMessage } from '../../lib/http';
import { useNotify } from '../../contexts/NotificationContext';
import { logger } from '../../lib/logger';

const BookLessonView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNewMode = id === 'new';

  // Pour création, chapterId vient des query params
  const chapterIdFromParams = searchParams.get('chapterId');

  const { environment: env } = useEnvironment();
  const [lesson, setLesson] = useState<BookLesson | null>(null);
  const [chapter, setChapter] = useState<BookChapter | null>(null);
  const [loading, setLoading] = useState(!isNewMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(isNewMode);
  const { notify } = useNotify('content');

  // Données du formulaire
  const [formData, setFormData] = useState<Partial<BookLesson>>({
    chapterId: chapterIdFromParams || '',
    lessonNumber: 1,
    title: '',
    description: '',
    contentSummary: '',
    estimatedDuration: undefined,
    exercisesCount: 0
  });

  // Charger la leçon existante
  const loadLesson = useCallback(async () => {
    if (isNewMode) return;

    try {
      setLoading(true);
      const data = await BookContentApiService.getBookLessonById(env, id!);
      setLesson(data);
      setFormData(data);

      // Charger le chapitre parent pour afficher son titre
      if (data.chapterId) {
        const chapterData = await SchoolBooksApiService.getBookChapterById(env, data.chapterId);
        setChapter(chapterData);
      }
    } catch (err: unknown) {
      setError('Erreur lors du chargement de la leçon');
    } finally {
      setLoading(false);
    }
  }, [id, env, isNewMode]);

  // Charger le chapitre parent (pour création)
  const loadParentChapter = useCallback(async () => {
    if (!isNewMode || !chapterIdFromParams) return;

    try {
      const chapterData = await SchoolBooksApiService.getBookChapterById(env, chapterIdFromParams);
      setChapter(chapterData);
    } catch (err) {
      logger.error('Erreur lors du chargement du chapitre parent:', err);
    }
  }, [chapterIdFromParams, env, isNewMode]);

  useEffect(() => {
    if (isNewMode) {
      loadParentChapter();
    } else if (id) {
      loadLesson();
    }
  }, [loadLesson, loadParentChapter, id, isNewMode]);

  // Handlers
  const handleChange = <Field extends keyof BookLesson>(field: Field, value: BookLesson[Field]) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (isNewMode) {
      navigate('/book-lessons');
    } else {
      if (lesson) {
        setFormData(lesson);
      }
      setIsEditMode(false);
      setError('');
    }
  };

  const validateForm = (): boolean => {
    if (!formData.title || formData.title.trim().length < 3) {
      setError('Le titre doit contenir au moins 3 caractères');
      return false;
    }
    if (!formData.description || formData.description.trim().length < 10) {
      setError('La description doit contenir au moins 10 caractères');
      return false;
    }
    if (!formData.contentSummary || formData.contentSummary.trim().length < 20) {
      setError('Le résumé du contenu doit contenir au moins 20 caractères');
      return false;
    }
    if (!formData.lessonNumber || formData.lessonNumber < 1) {
      setError('Le numéro de leçon doit être supérieur ou égal à 1');
      return false;
    }
    if (!formData.chapterId) {
      setError('Le chapitre parent est requis');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (isNewMode) {
        const response = await BookContentApiService.createBookLesson(env, formData as Omit<BookLesson, 'id'>);
        if (response.data?.id) {
          notify('Leçon créée avec succès', 'success');
          navigate(`/book-lessons/${response.data.id}`, { replace: true });
        } else {
          throw new Error(response.error || 'Erreur lors de la création');
        }
      } else {
        await BookContentApiService.updateBookLesson(env, id!, formData);
        setLesson({ ...lesson, ...formData } as BookLesson);
        setIsEditMode(false);
        notify('Leçon mise à jour avec succès', 'success');
      }
    } catch (err: unknown) {
      setError(toUserMessage(err, 'Erreur lors de la sauvegarde'));
      notify('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Vérifier chapterId pour création
  if (isNewMode && !chapterIdFromParams) {
    return (
      <Box p={3}>
        <Alert severity="error">Le paramètre chapterId est requis pour créer une leçon</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/book-lessons')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  // Error state
  if (!isNewMode && !lesson && !loading) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Leçon non trouvée'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/book-lessons')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/book-lessons')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
          {isNewMode
            ? 'Nouvelle Leçon'
            : (isEditMode
              ? 'Modifier la leçon'
              : `Leçon ${formData.lessonNumber}: ${formData.title}`
            )
          }
        </Typography>

        {!isEditMode && !isNewMode && (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Modifier
          </Button>
        )}

        {isEditMode && (
          <Box>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              sx={{ mr: 1 }}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Chapitre parent */}
      {chapter && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Chapitre : <strong>Chap. {chapter.chapterNumber}: {chapter.title}</strong>
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEditMode ? 'Informations de la leçon' : 'Détails'}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {isEditMode ? (
          /* Mode Édition */
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Numéro de la leçon"
                type="number"
                value={formData.lessonNumber || 1}
                onChange={(e) => handleChange('lessonNumber', parseInt(e.target.value) || 1)}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Durée estimée (minutes)"
                type="number"
                value={formData.estimatedDuration || ''}
                onChange={(e) => handleChange('estimatedDuration', e.target.value ? parseInt(e.target.value) : undefined)}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                helperText="Minimum 3 caractères"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                multiline
                rows={2}
                helperText="Minimum 10 caractères"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Résumé du contenu"
                value={formData.contentSummary || ''}
                onChange={(e) => handleChange('contentSummary', e.target.value)}
                required
                multiline
                rows={4}
                helperText="Résumé détaillé des notions abordées (minimum 20 caractères)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre d'exercices"
                value={formData.exercisesCount || 0}
                type="number"
                InputProps={{ readOnly: true }}
                helperText="Calculé automatiquement"
              />
            </Grid>
          </Grid>
        ) : (
          /* Mode Lecture */
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Numéro de la leçon
              </Typography>
              <Typography variant="body1">{formData.lessonNumber || '-'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Durée estimée
              </Typography>
              <Typography variant="body1">
                {formData.estimatedDuration ? `${formData.estimatedDuration} minutes` : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {formData.description || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Résumé du contenu
              </Typography>
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                {formData.contentSummary || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Nombre d'exercices
              </Typography>
              <Typography variant="body1">{formData.exercisesCount || 0}</Typography>
            </Grid>
          </Grid>
        )}
      </Paper>

    </Box>
  );
};

export default BookLessonView;
